import GPT from '@op/components/GPT';
import React, { useRef, useState } from 'react';
import { Form, notification } from 'tea-component';
import { useTopicInputDefaultValue } from '@op/components/FilterCommon/hooks/init';
import CardCommon from '@op/components/CardCommon';
import { useReportPermissionNotify } from '@op/components/ReportPermission';
import { buriedBtnPoint, DBO_POINT } from '@op/operationLog/buriedPoint';
import { KeywordUrlFilterProps } from '../../../types';
import { KeywordTopicOptions } from './components/KeywordOptions';
import GptOptions from './components/GptOptions';
import ResultsOutput from './components/ResultsOutput';
import CommonFormButtons from './components/CommonFormButtons';
import { useCommonFilter } from './hooks/useCommonFilter';
import { useFilterConfig } from './hooks/useFilterConfig';
import './index.scss';
import RegularListeningSetting from './components/RegularListeningSetting';
import {
  useCreateKeywordAnalysis,
  useStopTopicAnalysis,
  useSummaryConfig,
} from '../../../hook/useTopicAnalysisFlow';
import { FilterFixed } from '@op/components/FilterCommon';
import { DBO_TM } from '@op/utils/format';
import { useInjectionContext } from '../../../provider';
import TriggerModalSelect from './TriggerModalSelect';

const topicDefaultTime = DBO_TM.utcFilterRangeDay(29, 0);
const keywordDefaultTime = DBO_TM.utcFilterRangeDay(90, 0);
const filterValue = {};
export default function KeywordFilter({
  gameInfo,
  promptList,
  setLoading,
  loading,
  changeFilterValue,
  resetSaveId,
  wholeGameInfo,
  videoPromptList,
  tab,
  userInfo,
  setTranslateTopicMap,
  changeTab,
}: KeywordUrlFilterProps) {
  const { isTopicAnalysis } = useInjectionContext();
  const permissionNotify = useReportPermissionNotify();
  const taskIdRef = useRef();
  const filterRef = useRef(null);
  const tabType = useRef<'keyword' | 'topic'>(
    isTopicAnalysis ? 'topic' : 'keyword',
  );

  const [regularListeningVisible, setRegularListeningVisible] = useState(false);
  const { loading: permissionLoading, hasPermission } = useSummaryConfig();
  const [triggerModalType, setTriggerModalType] = useState('gemini_3_flash');
  // 根据 tabType 动态生成 analysisSuffix
  const analysisSuffix =
    tabType.current === 'topic' ? 'AI Topic Analysis' : 'Keyword Analysis';

  // 使用共同的 hook
  const {
    emailList,
    setShowEmailMessage,
    saveName,
    setSaveName,
    isSave,
    setIsSave,
    showEmailMessage,
    setEmailList,
    filterOnChange,
    promptInfo,
    setPromptInfo,
    videoPromptInfo,
    commentsPromptInfo,
    keywordPromptInfo,
    visible,
    toggleVisible,
    initPromptInfo,
    modalTitle,
    gptType,
    setGptType,
    remainder,
    total,
    advancedLimitLoading,
    getPromptInfo,
    resetPromptInfo,
    gptList,
    setGptList,
    realPromptList,
    isAdvanced,
    getPromptRealDetail,
    keywordCustomInput,
    setKeywordCustomInput,
    keywordSearchType,
    grammarTrue,
    grammarText,
    initCustomInputValue,
    getKeywordInfo,
    createShortUrl,
  } = useCommonFilter({
    tab,
    wholeGameInfo,
    gameInfo,
    promptList,
    videoPromptList,
    userInfo,
    analysisSuffix,
  });

  const {
    scrollBody,
    filterLoading,
    changeFilter,
    filterOptions,
    confirmRef,
    filterReset,
    filterSave,
    filterConfirm,
    confirmDisabled,
    saveDisabled,
    setSaveDisabled,
    isGenerated,
    setDisabled,
    maximumDuration,
  } = useFilterConfig({
    gameInfo,
    tab: tabType.current,
    grammarTrue,
    urlDisabled: false,
    hasUrlList: true,
    hasPermission,
  });

  const {
    value: topicCustomInput,
    setValue: setTopicCustomInput,
    disabled,
  } = useTopicInputDefaultValue({
    defaultValue: {},
    filterValue,
    tabType: tabType.current,
  });
  function initCustomTopicInfo(data: any) {
    setTopicCustomInput(data.query);
    // setTabType(data.tabType || 'keyword');
  }

  function getOtherInfo(isCreateShortid = false, filterPrompt = false) {
    const { keyword } = getPromptInfo();
    // 使用新接口创建临时任务
    const promptDetail = getPromptRealDetail(keyword);
    const keywordsParams = getKeywordInfo(isCreateShortid);
    const isTopic = tabType.current === 'topic';
    let obj: any = {
      model_type: isTopic ? triggerModalType : promptDetail?.modelType,
      prompt: promptDetail?.prompt || '',
      task_type: keyword?.gptOptions || 'basic',
      keyword_analysis_task_category: isTopic ? 'topic' : 'normal',
      data_type: 'new',
    };

    // topic 类型使用 query 参数，normal 类型使用 keyword_list 参数
    if (isTopic) {
      obj.query = topicCustomInput; 
    } else {
      obj.keywords_list = keywordsParams.keywords_list;
    }

    if (isCreateShortid) {
      obj = {
        ...obj,
        ...keywordsParams,
        promptInfo: getPromptInfo(filterPrompt),
        gptList,
        tabType: tabType.current,
        triggerModalType,
      };
    }
    return obj;
  }

  const { updateAnalysis: stopAnalysis, loadingRef: stopLoading } =
    useStopTopicAnalysis();
  const { createAnalysisTask } = useCreateKeywordAnalysis({ isCreator: true });

  async function stopRequest() {
    if (taskIdRef.current && !stopLoading.current) {
      // 暂停任务
      await stopAnalysis({ task_id: taskIdRef.current, ...gameInfo });
    }

    changeFilterValue({
      filter: null,
      oriFilter: null,
      pageFilter: null,
      tab,
      isSave,
      taskId: undefined,
    });
    taskIdRef.current = undefined;
    setLoading(false);
  }

  async function keywordConfirm(filter: any, saveFilter: any) {
    // 先清空TaskId，避免stop错了任务
    taskIdRef.current = undefined;

    const shortId = await createShortUrl(
      saveFilter,
      'Analysis Generate failed, please try again',
    );
    if (shortId) {
      // 处理参数：topic 类型需要 query 参数，不传 keyword_list
      const taskParams: any = {
        task_name: isSave ? saveName : '',
        keyword_analysis_task_type: 'temporary',
        short_url: shortId,
        should_save_report: isSave,
        need_summary: true,
        ...filter,
      };

      // return;
      // 创建分析报告后
      const createResponse = await createAnalysisTask(taskParams);

      const id = createResponse?.task_id;
      // 保存成功
      if (id) {
        taskIdRef.current = id;
        // 暂存权限目标，待内容区滚动到位后由 useReportCreateSuccess 弹出权限提示
        if (isSave) {
          permissionNotify?.setPendingReportTarget(createResponse);
        }
        changeFilterValue({
          filter,
          oriFilter: saveFilter,
          tab,
          isSave,
          promptInfo: { keyword: keywordPromptInfo },
          pageFilter: {
            id,
            short_url: shortId,
            category:
              tabType.current === 'keyword' ? 'keyword' : 'keyword_topic',
            ...gameInfo,
          },
          taskId: id,
        });
        return;
      }
    }

    notification.error({ description: '创建分析任务失败' });
    stopRequest();
  }

  async function confirm(filterData: any, oriFilterValue: any) {
    setLoading(true);
    const filter = {
      ...getOtherInfo(),
      ...filterData,
    };
    const saveFilter = {
      ...getOtherInfo(true),
      ...oriFilterValue,
    };
    try {
      await keywordConfirm(filter, saveFilter);
    } catch (error) {
      console.error('确认关键词失败:', error);
    }
    const operationLog = { ...getOtherInfo(true, true), ...oriFilterValue };
    buriedBtnPoint(
      'GenerateReport',
      operationLog,
      operationLog?.keywordCustomInput,
      `${operationLog?.content_urls}`,
    );
  }
  return (
    <div className="keyword-url-filter-wrapper">
      <FilterFixed
        filter={filterOptions}
        confirmRef={confirmRef}
        scrollBody={scrollBody}
        keyOperateShowOptions={[]}
        operateShowOptions={['more', 'history']}
        beforeCacheDealwithData={() => false}
        maximumDuration={maximumDuration as any}
        onlySaveFilters={['keyWords']}
        defaultValue={isTopicAnalysis ? topicDefaultTime : keywordDefaultTime}
        onChange={(value: any) => {
          filterOnChange(value);
          filterRef.current = value;
        }}
        beforeSaveFilterSetting={(data) => {
          const { value, fixedHeader } = data;
          value.keywordActiveId = 'custom';
          value.keywordCustomInput = keywordCustomInput;
          value.keywordSearchType = keywordSearchType;
          fixedHeader.keywordActiveId = 'custom';
          fixedHeader.keywordCustomInput = keywordCustomInput;
          fixedHeader.keywordSearchType = keywordSearchType;
          return data;
        }}
        historyFilterChange={(_name: string, data: any) => {
          initCustomInputValue(data);
        }}
        finallyDealwithDefaultValue={(
          data: any,
          _otherPageParams?: any,
          isDefaultValue?: boolean,
        ) => {
          if (!isDefaultValue) {
            initCustomInputValue(data);
            initPromptInfo(data);
            initCustomTopicInfo(data);
          }
          return data;
        }}
        toggleSaveBtnDisable={(value) => setSaveDisabled(value)}
        confirm={(value: any, oriFilterValue: any) => {
          const filterData = { ...value, ...gameInfo };
          changeFilter(filterData);

          function commonConfirm() {
            confirm(filterData, oriFilterValue);
            isGenerated.current = false;
          }

          if (isGenerated.current) {
            commonConfirm();
          }
        }}
        backCondOriginData={(data) => {
          setTranslateTopicMap?.(data?.topic_trans || []);
        }}
        confirmDisabledOnChange={(value: boolean) => setDisabled(value)}
      />

      <CardCommon
        loading={filterLoading || permissionLoading}
        loadingHeight={240}
        className="dbo-keywords-analysis-form"
      >
        <Form>
          <KeywordTopicOptions
            gameInfo={gameInfo}
            keywordGrammarText={grammarText}
            keywordGrammarTrue={grammarTrue}
            keywordInput={keywordCustomInput}
            setKeywordInput={setKeywordCustomInput}
            tab={tabType.current}
            topicInput={topicCustomInput}
            setTopicInput={setTopicCustomInput}
            loading={loading}
            isTopicAnalysis={isTopicAnalysis}
          ></KeywordTopicOptions>
          {tabType.current === 'topic' && (
            <TriggerModalSelect
              gptType={triggerModalType}
              setGptType={setTriggerModalType}
            ></TriggerModalSelect>
          )}
          <GptOptions
            remainder={remainder}
            total={total}
            gptType={gptType}
            setGptType={setGptType}
            toggleVisible={toggleVisible}
            setPromptInfo={setPromptInfo}
            videoPromptInfo={videoPromptInfo}
            commentsPromptInfo={commentsPromptInfo}
            keywordPromptInfo={keywordPromptInfo}
            advancedLimitLoading={advancedLimitLoading}
            gptList={gptList}
            setGptList={setGptList}
            tab={tab}
          ></GptOptions>
          <ResultsOutput
            name={saveName}
            setName={setSaveName}
            isSave={isSave}
            setIsSave={setIsSave}
            emailList={emailList}
            setEmailList={setEmailList}
            setShowEmailMessage={setShowEmailMessage}
            showEmailMessage={showEmailMessage}
            gameInfo={gameInfo}
          ></ResultsOutput>
          <CommonFormButtons
            gameInfo={gameInfo}
            loading={loading}
            confirmDisabled={!!confirmDisabled || disabled}
            saveDisabled={saveDisabled}
            saveName={saveName}
            showEmailMessage={showEmailMessage}
            isSave={isSave}
            onGenerate={() => {
              isGenerated.current = true;
              resetSaveId();
              setTimeout(() => {
                filterConfirm(true);
              }, 500);
            }}
            onReset={() => {
              filterReset();
              initCustomInputValue({});
              resetPromptInfo();
            }}
            onSave={() => {
              filterSave();
            }}
            onStop={() => void stopRequest()}
            showRegularListening={() => {
              DBO_POINT.btn('RegularListeningConfirm');
              setRegularListeningVisible(true);
            }}
            tabType={tabType.current}
          />
        </Form>
      </CardCommon>

      <GPT.PromptModalSimple
        visible={visible}
        close={() => toggleVisible()}
        handleConfirm={(data) => {
          const { prompt, ...otherInfo } = data;
          const key = isAdvanced ? 'advancedPrompt' : 'prompt';
          setPromptInfo(
            { ...promptInfo, ...otherInfo, [key]: prompt },
            'keyword',
          );
          toggleVisible();
        }}
        isAdvanced={isAdvanced}
        title={modalTitle}
        promptList={realPromptList}
        gptType={gptType}
        defaultPromptText={
          isAdvanced ? promptInfo?.advancedPrompt : promptInfo?.prompt
        }
        defaultActive={promptInfo?.prompt ? -1 : 1}
      ></GPT.PromptModalSimple>
      {regularListeningVisible && (
        <RegularListeningSetting
          visible={regularListeningVisible}
          onClose={() => setRegularListeningVisible(false)}
          hasPermission={hasPermission}
          onConfirm={(data) => {
            buriedBtnPoint('ConfirmRegularListening', data);
            setRegularListeningVisible(false);
            changeTab?.(tabType.current);
          }}
          currentFilter={{
            ...getOtherInfo(true),
            ...(filterRef?.current || {}),
          }}
          gameInfo={gameInfo}
          category={tabType.current === 'topic' ? 'topic' : 'normal'}
          defaultName={saveName}
        />
      )}
    </div>
  );
}
